import { useState, useEffect, useRef, Component } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { 
  ChevronLeft, Gauge, Fuel, Settings2, Palette, Calendar, 
  MapPin, Phone, Mail, Check, Car, Clock, X, ZoomIn, ChevronRight, Search, CircleDot, Share2, Copy, Play
} from 'lucide-react';
import { Navbar } from '../components/layout/Navbar';
import { Footer } from '../components/layout/Footer';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { trackPageView, trackCTAClick, trackFormSubmit, trackCallClick, addRecentlyViewed } from '../utils/analytics';
import { RecentlyViewed } from '../components/RecentlyViewed';
import { SEO, generateVehicleSchema, generateBreadcrumbSchema } from '../components/SEO';
import { LiveViewers } from '../components/LiveViewers';
import { CarfaxReport, hasCarfaxReport, isCarfaxAccidentFree, isCarfaxOneOwner } from '../components/CarfaxReport';
import GoogleRating from '../components/GoogleRating';
import ReserveModal from '../components/ReserveModal';
import InspectionReport from '../components/InspectionReport';
import PriceDropAlert from '../components/PriceDropAlert';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Error boundary to prevent blank pages on mobile
class VehicleErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  componentDidCatch(error, info) {
    console.error('VehicleDetailPage crash:', error, info);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex flex-col">
          <div className="flex-1 flex items-center justify-center p-8 text-center">
            <div>
              <h2 className="text-xl font-bold text-slate-800 mb-2">Une erreur est survenue</h2>
              <p className="text-slate-600 mb-4">Impossible d'afficher ce véhicule.</p>
              <a href="/inventory" className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg">
                Retour à l'inventaire
              </a>
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

// Helper to normalize image URLs - handles relative paths, full URLs, and proxied external images
const getImageUrl = (url) => {
  if (!url) return null;
  
  // Proxy external images that might have hotlinking protection
  if (url.includes('image.autousagee.ca')) {
    return `${API}/proxy/image?url=${encodeURIComponent(url)}`;
  }
  
  // Convert relative paths to full URLs (e.g., /api/uploads/... -> full URL)
  if (url.startsWith('/api/')) {
    return `${BACKEND_URL}${url}`;
  }
  
  // Return absolute URLs as-is (already includes domain)
  return url;
};

// ============ LIGHTBOX WITH ZOOM LENS COMPONENT ============
const ImageLightbox = ({ images: photoImages, videoUrl, initialIndex, onClose, vehicleName }) => {
  const images = videoUrl ? [{ video: videoUrl }, ...photoImages] : photoImages;
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [showLens, setShowLens] = useState(false);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const [imgRect, setImgRect] = useState(null);
  const [naturalSize, setNaturalSize] = useState({ width: 0, height: 0 });
  const [loadedImgSrc, setLoadedImgSrc] = useState('');
  const imageRef = useRef(null);
  const containerRef = useRef(null);
  const lensSize = 220; // Lens diameter in pixels
  const zoomLevel = 2; // Magnification level - balanced for image quality

  const currentImage = images[currentIndex];
  const isVideoSlide = Boolean(currentImage?.video);
  const imgUrl = isVideoSlide ? '' : typeof currentImage === 'object' ? currentImage.url : getImageUrl(currentImage);

  const handlePrev = () => {
    setCurrentIndex((prev) => (prev > 0 ? prev - 1 : images.length - 1));
    setShowLens(false);
    setLoadedImgSrc('');
    setNaturalSize({ width: 0, height: 0 });
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev < images.length - 1 ? prev + 1 : 0));
    setShowLens(false);
    setLoadedImgSrc('');
    setNaturalSize({ width: 0, height: 0 });
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') onClose();
    if (e.key === 'ArrowLeft') handlePrev();
    if (e.key === 'ArrowRight') handleNext();
  };

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'auto';
    };
  }, []);

  const handleImageLoad = (e) => {
    const img = e.target;
    if (imageRef.current) {
      setImgRect(imageRef.current.getBoundingClientRect());
      setNaturalSize({ width: img.naturalWidth, height: img.naturalHeight });
      setLoadedImgSrc(img.src);
    }
  };

  const handleMouseMove = (e) => {
    if (!imageRef.current || !loadedImgSrc) return;
    
    const rect = imageRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    
    // Check if mouse is within image bounds
    if (x < 0 || x > rect.width || y < 0 || y > rect.height) {
      setShowLens(false);
      return;
    }
    
    setShowLens(true);
    setMousePos({ x, y });
    setImgRect(rect);
  };

  const handleMouseEnter = () => {
    if (imageRef.current) {
      setImgRect(imageRef.current.getBoundingClientRect());
    }
  };

  // Calculate lens position and background for zoom effect
  // Uses natural image dimensions for sharper zoom
  const getLensStyle = () => {
    if (!imgRect || !loadedImgSrc || !naturalSize.width) return {};
    
    // Position lens centered on mouse
    const lensLeft = mousePos.x - lensSize / 2;
    const lensTop = mousePos.y - lensSize / 2;
    
    // Calculate the scale factor between displayed and natural image
    const displayToNaturalX = naturalSize.width / imgRect.width;
    const displayToNaturalY = naturalSize.height / imgRect.height;
    
    // For the zoom, we scale up the displayed image
    // Using the displayed image dimensions ensures correct alignment
    const bgWidth = imgRect.width * zoomLevel;
    const bgHeight = imgRect.height * zoomLevel;
    
    // Calculate background position to center the zoomed area in the lens
    const bgX = -(mousePos.x * zoomLevel - lensSize / 2);
    const bgY = -(mousePos.y * zoomLevel - lensSize / 2);
    
    return {
      position: 'absolute',
      left: `${lensLeft}px`,
      top: `${lensTop}px`,
      width: `${lensSize}px`,
      height: `${lensSize}px`,
      borderRadius: '50%',
      border: '3px solid white',
      boxShadow: '0 4px 20px rgba(0,0,0,0.6), inset 0 0 10px rgba(0,0,0,0.2)',
      pointerEvents: 'none',
      overflow: 'hidden',
      backgroundImage: `url("${loadedImgSrc}")`,
      backgroundRepeat: 'no-repeat',
      backgroundSize: `${bgWidth}px ${bgHeight}px`,
      backgroundPosition: `${bgX}px ${bgY}px`,
      zIndex: 10,
      // Improve rendering quality
      imageRendering: 'auto',
      WebkitBackfaceVisibility: 'hidden',
      backfaceVisibility: 'hidden',
    };
  };

  return (
    <div 
      className="fixed inset-0 z-[10000] bg-black/95 flex items-center justify-center"
      onClick={onClose}
      data-testid="lightbox-overlay"
    >
      {/* Close button */}
      <button
        onClick={onClose}
        className="absolute top-4 right-4 text-white/80 hover:text-white z-50 p-2 bg-black/50 rounded-full"
        data-testid="lightbox-close"
      >
        <X className="h-8 w-8" />
      </button>

      {/* Image counter */}
      <div className="absolute top-4 left-4 text-white bg-black/60 px-4 py-2 rounded-full text-sm font-medium">
        {currentIndex + 1} / {images.length}
      </div>

      {/* Zoom indicator */}
      <div className={`${isVideoSlide ? 'hidden' : ''} absolute top-4 left-1/2 -translate-x-1/2 text-white bg-blue-600 px-4 py-2 rounded-full flex items-center gap-2 text-sm font-medium`}>
        <Search className="h-4 w-4" />
        Survoler l'image pour zoomer ({zoomLevel}x)
      </div>

      {/* Previous button */}
      <button
        onClick={(e) => { e.stopPropagation(); handlePrev(); }}
        className="absolute left-4 top-1/2 -translate-y-1/2 text-white hover:bg-white/20 p-3 bg-black/50 rounded-full z-50 transition-colors"
        data-testid="lightbox-prev"
      >
        <ChevronLeft className="h-8 w-8" />
      </button>

      {/* Main image container with zoom */}
      <div 
        ref={containerRef}
        className="relative select-none"
        onClick={(e) => e.stopPropagation()}
        onMouseMove={isVideoSlide ? undefined : handleMouseMove}
        onMouseEnter={isVideoSlide ? undefined : handleMouseEnter}
        onMouseLeave={() => setShowLens(false)}
        style={{ cursor: isVideoSlide ? 'default' : 'crosshair' }}
        data-testid="lightbox-image-container"
      >
        {isVideoSlide ? (
          <video
            key={currentImage.video}
            src={currentImage.video}
            controls
            autoPlay
            muted
            loop
            playsInline
            className="max-w-[85vw] max-h-[75vh] object-contain rounded-lg bg-black"
            data-testid="lightbox-video"
          />
        ) : (
        <img
          ref={imageRef}
          src={imgUrl}
          alt={vehicleName}
          className="max-w-[85vw] max-h-[75vh] object-contain rounded-lg"
          draggable={false}
          onLoad={handleImageLoad}
          data-testid="lightbox-main-image"
        />
        )}

        {/* Magnifying lens with zoomed content */}
        {showLens && imgRect && loadedImgSrc && naturalSize.width > 0 && (
          <div style={getLensStyle()} data-testid="zoom-lens">
            {/* Crosshair overlay */}
            <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
              <div className="absolute left-1/2 top-0 bottom-0 w-px bg-white/50 -translate-x-1/2" />
              <div className="absolute top-1/2 left-0 right-0 h-px bg-white/50 -translate-y-1/2" />
              <div className="w-2 h-2 border border-white/70 rounded-full" />
            </div>
          </div>
        )}
      </div>

      {/* Next button */}
      <button
        onClick={(e) => { e.stopPropagation(); handleNext(); }}
        className="absolute right-4 top-1/2 -translate-y-1/2 text-white hover:bg-white/20 p-3 bg-black/50 rounded-full z-50 transition-colors"
        data-testid="lightbox-next"
      >
        <ChevronRight className="h-8 w-8" />
      </button>

      {/* Thumbnail strip */}
      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 bg-black/70 p-3 rounded-xl max-w-[90vw] overflow-x-auto">
        {images.map((img, index) => {
          if (img?.video) {
            return (
              <button
                key="video"
                onClick={(e) => { e.stopPropagation(); setCurrentIndex(index); setShowLens(false); }}
                className={`relative flex-shrink-0 w-20 h-14 rounded-lg overflow-hidden border-2 transition-all bg-black ${
                  index === currentIndex ? 'border-white scale-105' : 'border-transparent opacity-50 hover:opacity-100'
                }`}
                data-testid="lightbox-thumb-video"
              >
                <video src={img.video} muted playsInline preload="metadata" className="w-full h-full object-cover pointer-events-none" />
                <span className="absolute inset-0 flex items-center justify-center bg-black/30">
                  <Play className="h-4 w-4 text-white fill-white" />
                </span>
              </button>
            );
          }
          const photoIndex = videoUrl ? index - 1 : index;
          const thumbUrl = typeof img === 'object' ? img.url : getImageUrl(img);
          return (
            <button
              key={index}
              onClick={(e) => { e.stopPropagation(); setCurrentIndex(index); setShowLens(false); }}
              className={`flex-shrink-0 w-20 h-14 rounded-lg overflow-hidden border-2 transition-all ${
                index === currentIndex ? 'border-white scale-105' : 'border-transparent opacity-50 hover:opacity-100'
              }`}
              data-testid={`lightbox-thumb-${photoIndex}`}
            >
              <img src={thumbUrl} alt="" className="w-full h-full object-cover" />
            </button>
          );
        })}
      </div>
    </div>
  );
};

const VehicleDetailPage = () => {
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const [vehicle, setVehicle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(0);
  const [showVideo, setShowVideo] = useState(true);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [globalMarketingImages, setGlobalMarketingImages] = useState([]);
  const [globalPromoImages, setGlobalPromoImages] = useState([]);
  const [overlaySettings, setOverlaySettings] = useState({ enabled: false, image_url: null });
  const [similarVehicles, setSimilarVehicles] = useState([]);
  const [activeTab, setActiveTab] = useState('inquiry');
  const [activeInfoTab, setActiveInfoTab] = useState('equipements');
  const [inquiryForm, setInquiryForm] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
  });
  const [testDriveForm, setTestDriveForm] = useState({
    name: '',
    email: '',
    phone: '',
    preferred_date: '',
    preferred_time: '',
    message: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [translatedDesc, setTranslatedDesc] = useState(null);
  const [translatingDesc, setTranslatingDesc] = useState(false);
  const [reserveOpen, setReserveOpen] = useState(false);
  
  // Touch swipe state using refs (persist across re-renders)
  const touchRef = useRef({ startX: 0, startY: 0, isSwiping: false });

  useEffect(() => {
    fetchVehicle();
    fetchGlobalMarketing();
  }, [id]);

  useEffect(() => {
    if (vehicle) {
      fetchSimilarVehicles();
    }
  }, [vehicle]);

  // Translate description on-the-fly when language is not French
  useEffect(() => {
    if (!vehicle?.description || i18n.language === 'fr') {
      setTranslatedDesc(null);
      return;
    }
    let cancelled = false;
    const translateDesc = async () => {
      setTranslatingDesc(true);
      try {
        const res = await axios.post(`${API}/translate`, {
          text: vehicle.description,
          target_lang: i18n.language,
          source_id: vehicle.stock_number || vehicle.vin || '',
        });
        if (!cancelled) setTranslatedDesc(res.data.translated);
      } catch {
        if (!cancelled) setTranslatedDesc(null);
      } finally {
        if (!cancelled) setTranslatingDesc(false);
      }
    };
    translateDesc();
    return () => { cancelled = true; };
  }, [vehicle?.description, i18n.language]);


  const fetchSimilarVehicles = async () => {
    try {
      // Fetch all available vehicles
      const response = await axios.get(`${API}/vehicles?status=available`);
      const allVehicles = response.data;
      
      // Filter out current vehicle and get various comparable vehicles
      const candidates = allVehicles.filter(v => {
        if (v.id === vehicle.id) return false; // Exclude current vehicle
        
        const sameBodyType = v.body_type === vehicle.body_type;
        const similarPrice = Math.abs(v.price - vehicle.price) <= 10000; // Wider price range
        const similarYear = Math.abs(v.year - vehicle.year) <= 5; // Wider year range
        
        // Include if similar body type, price range, or year - variety over same make
        return sameBodyType || similarPrice || similarYear;
      });
      
      // Sort by: 1) Oldest stock first (prioritize old inventory), 2) Then by relevance
      candidates.sort((a, b) => {
        // Primary sort: older stock first (earlier created_at)
        const aDate = new Date(a.created_at || '2099-01-01');
        const bDate = new Date(b.created_at || '2099-01-01');
        const dateDiff = aDate - bDate;
        
        // If dates are significantly different (more than 7 days), prioritize older
        if (Math.abs(dateDiff) > 7 * 24 * 60 * 60 * 1000) {
          return dateDiff;
        }
        
        // Secondary sort: relevance score (body type and price similarity)
        const aScore = (a.body_type === vehicle.body_type ? 2 : 0) +
                       (Math.abs(a.price - vehicle.price) <= 5000 ? 1 : 0);
        const bScore = (b.body_type === vehicle.body_type ? 2 : 0) +
                       (Math.abs(b.price - vehicle.price) <= 5000 ? 1 : 0);
        return bScore - aScore;
      });
      
      // Ensure variety - try to include different makes
      const selected = [];
      const makesUsed = new Set();
      
      for (const v of candidates) {
        if (selected.length >= 4) break;
        
        // Prioritize variety in makes, but don't skip if we need more vehicles
        if (!makesUsed.has(v.make) || selected.length < 2 || candidates.length <= 4) {
          selected.push(v);
          makesUsed.add(v.make);
        }
      }
      
      // If we still need more, add remaining candidates
      if (selected.length < 4) {
        for (const v of candidates) {
          if (selected.length >= 4) break;
          if (!selected.includes(v)) {
            selected.push(v);
          }
        }
      }
      
      setSimilarVehicles(selected);
    } catch (error) {
      console.error('Error fetching similar vehicles:', error);
    }
  };

  const fetchVehicle = async () => {
    try {
      const response = await axios.get(`${API}/vehicles/${id}`);
      setVehicle(response.data);
      
      // Track page view and add to recently viewed
      trackPageView('vehicle_detail', id);
      addRecentlyViewed(response.data);
    } catch (error) {
      console.error('Error fetching vehicle:', error);
      toast.error('Véhicule non trouvé');
    } finally {
      setLoading(false);
    }
  };

  const fetchGlobalMarketing = async () => {
    try {
      const [marketingRes, promoRes, overlayRes] = await Promise.all([
        axios.get(`${API}/marketing/global-images`),
        axios.get(`${API}/marketing/promo-images`),
        axios.get(`${API}/marketing/overlay`),
      ]);
      setGlobalMarketingImages(marketingRes.data);
      setGlobalPromoImages(promoRes.data);
      setOverlaySettings(overlayRes.data);
    } catch (error) {
      console.error('Error fetching marketing data:', error);
    }
  };

  const handleInquirySubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/inquiries`, {
        ...inquiryForm,
        vehicle_id: id,
      });
      
      // Track inquiry submission
      trackFormSubmit('inquiry', id);
      
      toast.success('Demande envoyée avec succès! Nous vous contacterons bientôt.');
      setInquiryForm({ name: '', email: '', phone: '', message: '' });
    } catch (error) {
      toast.error("Échec de l'envoi. Veuillez réessayer.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleTestDriveSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/test-drives`, {
        vehicle_id: id,
        name: testDriveForm.name,
        email: testDriveForm.email,
        phone: testDriveForm.phone,
        preferred_date: testDriveForm.preferred_date,
        preferred_time: testDriveForm.preferred_time,
        message: testDriveForm.message,
      });
      
      // Track test drive submission
      trackFormSubmit('test_drive', id);
      
      toast.success(t('vehicle.testDriveSuccess'));
      setTestDriveForm({ name: '', email: '', phone: '', preferred_date: '', preferred_time: '', message: '' });
    } catch (error) {
      toast.error("Échec de la réservation. Veuillez réessayer.");
    } finally {
      setSubmitting(false);
    }
  };

  const formatPrice = (price) => {
    if (!price || price === 0) {
      return t('vehicle.callDealer');
    }
    return new Intl.NumberFormat(i18n.language === 'en' ? 'en-CA' : i18n.language === 'es' ? 'es' : i18n.language === 'ar' ? 'ar' : 'fr-CA', {
      style: 'currency',
      currency: 'CAD',
      minimumFractionDigits: 0,
    }).format(price);
  };

  const formatMileage = (mileage) => {
    return new Intl.NumberFormat(i18n.language === 'en' ? 'en-CA' : 'fr-CA').format(mileage);
  };

  const [shareCopied, setShareCopied] = useState(false);
  const handleShareVehicle = async () => {
    // Use current origin so it works on any domain (production, preview, etc.)
    const shareUrl = `${window.location.origin}/api/share/vehicle/${id}`;
    try {
      if (navigator.share) {
        await navigator.share({
          title: `${vehicle.year} ${vehicle.make} ${vehicle.model} ${vehicle.trim || ''}`,
          text: `${vehicle.year} ${vehicle.make} ${vehicle.model} - Entrepôt Auto Montréal`,
          url: shareUrl
        });
      } else {
        await navigator.clipboard.writeText(shareUrl);
        setShareCopied(true);
        toast.success(t('vehicle.linkCopied', 'Lien copié!'));
        setTimeout(() => setShareCopied(false), 2000);
      }
    } catch {
      try {
        await navigator.clipboard.writeText(shareUrl);
        setShareCopied(true);
        toast.success(t('vehicle.linkCopied', 'Lien copié!'));
        setTimeout(() => setShareCopied(false), 2000);
      } catch { /* ignore */ }
    }
  };

  const translateFuel = (fuel) => {
    if (!fuel) return '-';
    const f = fuel.toLowerCase();
    const map = { 
      gasoline: t('vehicle.gasoline'), essence: t('vehicle.gasoline'),
      diesel: t('vehicle.diesel'), 
      electric: t('vehicle.electric'), électrique: t('vehicle.electric'), electrique: t('vehicle.electric'),
      hybrid: t('vehicle.hybrid'), hybride: t('vehicle.hybrid')
    };
    return map[f] || fuel;
  };

  const translateTransmission = (trans) => {
    if (!trans) return t('vehicle.automatic');
    const val = trans.toLowerCase();
    if (val === 'automatic' || val === 'auto' || val === 'automatique') return t('vehicle.automatic');
    if (val === 'manual' || val === 'manuelle' || val === 'manuel') return t('vehicle.manual');
    if (val === 'cvt') return t('vehicle.cvt');
    return t('vehicle.manual');
  };

  const translateBodyType = (type) => {
    if (!type) return '-';
    const val = type.toLowerCase();
    const map = { 
      sedan: t('vehicle.sedan'), berline: t('vehicle.sedan'),
      suv: t('vehicle.suv'), vus: t('vehicle.suv'),
      truck: t('vehicle.truck'), camion: t('vehicle.truck'),
      coupe: t('vehicle.coupe'), coupé: t('vehicle.coupe'),
      hatchback: t('vehicle.hatchback'), hayon: t('vehicle.hatchback'),
      van: t('vehicle.van'), fourgon: t('vehicle.van'), fourgonnette: t('vehicle.van'),
      convertible: t('vehicle.convertible'), cabriolet: t('vehicle.convertible')
    };
    return map[val] || type;
  };

  const translateDrivetrain = (dt) => {
    if (!dt) return '-';
    const val = dt.toLowerCase();
    if (val === 'fwd') return t('vehicle.fwd');
    if (val === 'rwd') return t('vehicle.rwd');
    if (val === 'awd') return t('vehicle.awd');
    if (val === '4wd' || val === '4x4') return t('vehicle.4wd');
    return dt;
  };

  const defaultImages = [
    // 3:2 ratio placeholder - fits perfectly
    'https://res.cloudinary.com/dtqyyb6su/image/upload/v1774460688/entrepot/placeholders/new_arrival_placeholder.webp',
  ];

  // Disclaimer image appended as last photo for all vehicles
  const DISCLAIMER_IMAGE = "https://res.cloudinary.com/dtqyyb6su/image/upload/v1774460840/entrepot/assets/disclaimer_image.png";

  // Merge vehicle images with global marketing and promo images at specified positions
  const getMergedImages = () => {
    const vehicleImages = vehicle?.images?.length > 0 ? vehicle.images : defaultImages;
    
    // Combine global marketing images (permanent) and global promo images (with end dates)
    const allMarketingImages = [
      ...globalMarketingImages.map(img => ({ ...img, isMarketing: true })),
      ...globalPromoImages.map(img => ({ ...img, isPromo: true })),
    ];
    
    if (allMarketingImages.length === 0) {
      const result = vehicleImages.map(img => ({ url: getImageUrl(img), isVehicle: true }));
      result.push({ url: DISCLAIMER_IMAGE, isDisclaimer: true });
      return result;
    }
    
    // Create result array with vehicle images - proxy external URLs
    const result = vehicleImages.map(img => ({ url: getImageUrl(img), isVehicle: true }));
    
    // Sort by position and insert marketing/promo images
    allMarketingImages.sort((a, b) => a.position - b.position).forEach(marketing => {
      const insertIndex = Math.min(marketing.position - 1, result.length);
      result.splice(insertIndex, 0, { 
        url: marketing.image_url, 
        isMarketing: marketing.isMarketing || false,
        isPromo: marketing.isPromo || false,
        name: marketing.name
      });
    });
    
    // Add disclaimer as the very last image
    result.push({ url: DISCLAIMER_IMAGE, isDisclaimer: true });
    
    return result;
  };

  // Fetch similar vehicles for "not found" state
  const [notFoundSimilar, setNotFoundSimilar] = useState([]);
  
  useEffect(() => {
    if (!loading && !vehicle) {
      // Fetch some vehicles to suggest when vehicle not found
      const fetchNotFoundSuggestions = async () => {
        try {
          const response = await axios.get(`${API}/vehicles?status=available&limit=8`);
          setNotFoundSimilar(response.data.slice(0, 8));
        } catch (error) {
          console.error('Error fetching suggestions:', error);
        }
      };
      fetchNotFoundSuggestions();
    }
  }, [loading, vehicle]);

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <div className="flex-1 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  if (!vehicle) {
    return (
      <div className="min-h-screen flex flex-col bg-gradient-to-b from-slate-50 to-white">
        <SEO 
          title="Véhicule Vendu - Entrepôt Auto Montréal"
          description="Ce véhicule a été vendu. Découvrez notre inventaire de véhicules d'occasion de qualité à Montréal."
          url="https://entrepotautomontreal.ca/inventory"
        />
        <Navbar />
        
        {/* Hero Section */}
        <div className="bg-gradient-to-r from-slate-800 to-slate-900 text-white py-16">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <Check className="h-10 w-10 text-green-400" />
            </div>
            <h1 className="text-3xl md:text-4xl font-bold mb-4" style={{ fontFamily: "'Montserrat', sans-serif" }}>
              {t('vehicle.vehicleSold')}
            </h1>
            <p className="text-lg text-slate-300 mb-8 max-w-2xl mx-auto">
              {t('vehicle.vehicleSoldDesc')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button asChild size="lg" className="bg-blue-600 hover:bg-blue-700">
                <Link to="/inventory">
                  <Search className="mr-2 h-5 w-5" />
                  {t('vehicle.seeInventory')}
                </Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-slate-900">
                <a href="tel:+18339956477">
                  <Phone className="mr-2 h-5 w-5" />
                  {t('vehicle.call_us')}
                </a>
              </Button>
            </div>
          </div>
        </div>
        
        {/* Similar Vehicles */}
        {notFoundSimilar.length > 0 && (
          <div className="max-w-7xl mx-auto px-4 py-12">
            <h2 className="text-2xl font-bold text-slate-800 mb-6 text-center">
              {t('vehicle.similarVehicles')}
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {notFoundSimilar.map((v) => (
                <Link 
                  key={v.id} 
                  to={`/vehicle/${v.id}`}
                  className="bg-white rounded-xl overflow-hidden border border-slate-200 shadow-sm hover:shadow-lg transition-all group"
                >
                  <div className="aspect-[4/3] overflow-hidden bg-slate-100">
                    <img 
                      src={v.images?.[0] || 'https://res.cloudinary.com/dtqyyb6su/image/upload/v1774460688/entrepot/placeholders/new_arrival_placeholder.webp'} 
                      alt={`${v.year} ${v.make} ${v.model}`}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                  <div className="p-3">
                    <h3 className="font-semibold text-slate-800 text-sm truncate">
                      {v.year} {v.make} {v.model}
                    </h3>
                    <p className="text-xs text-slate-500">{v.mileage?.toLocaleString()} km</p>
                    {v.price > 0 && (
                      <p className="text-blue-600 font-bold mt-1">
                        {new Intl.NumberFormat('fr-CA', { style: 'currency', currency: 'CAD', minimumFractionDigits: 0 }).format(v.price)}
                      </p>
                    )}
                  </div>
                </Link>
              ))}
            </div>
            <div className="text-center mt-8">
              <Button asChild size="lg">
                <Link to="/inventory">Voir tous les véhicules</Link>
              </Button>
            </div>
          </div>
        )}
        
        {/* Contact CTA */}
        <div className="bg-blue-600 text-white py-10">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <h3 className="text-xl font-bold mb-2">Vous cherchez un véhicule spécifique?</h3>
            <p className="text-blue-100 mb-6">{t('vehicle.contactUsHelp')}</p>
            <Button asChild variant="secondary" size="lg">
              <Link to="/contact">{t('vehicle.contactUs')}</Link>
            </Button>
          </div>
        </div>
        
        <Footer />
      </div>
    );
  }

  const images = getMergedImages();
  
  // Generate breadcrumb schema
  const breadcrumbItems = [
    { name: "Accueil", url: "https://entrepotautomontreal.ca" },
    { name: "Inventaire", url: "https://entrepotautomontreal.ca/inventory" },
    { name: `${vehicle.year} ${vehicle.make} ${vehicle.model}`, url: `https://entrepotautomontreal.ca/vehicles/${vehicle.id}` }
  ];

  return (
    <div className="min-h-screen flex flex-col" data-testid="vehicle-detail-page">
      <SEO 
        title={vehicle.seo_meta_title || `${vehicle.year} ${vehicle.make} ${vehicle.model} ${vehicle.trim || ''} - ${formatMileage(vehicle.mileage || vehicle.km)} km`}
        description={vehicle.seo_meta_description || `${vehicle.year} ${vehicle.make} ${vehicle.model} ${vehicle.trim || ''} à vendre chez Entrepôt Auto Montréal. ${formatMileage(vehicle.mileage || vehicle.km)} km, ${vehicle.transmission || 'Automatique'}, ${vehicle.exterior_color || ''}. ${formatPrice(vehicle.price || vehicle.online_price)}. Financement disponible.`}
        url={`https://entrepotautomontreal.ca/vehicle/${vehicle.id}`}
        image={images[0]?.url || vehicle.images?.[0] || 'https://entrepotautomontreal.ca/og-image.png'}
        type="product"
        keywords={vehicle.seo_keywords?.join(', ') || `${vehicle.year} ${vehicle.make} ${vehicle.model}, ${vehicle.make} occasion, ${vehicle.model} usagé, voiture ${vehicle.exterior_color}, auto montréal`}
        schema={generateVehicleSchema(vehicle)}
      />
      <Navbar />

      {/* Breadcrumb */}
      <div className="bg-muted border-b border-border">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-3">
          <Link to="/inventory" className="inline-flex items-center text-sm text-muted-foreground hover:text-primary transition-colors" data-testid="back-to-inventory">
            <ChevronLeft className="h-4 w-4 mr-1" />
            {t('vehicle.backToInventory')}
          </Link>
        </div>
      </div>

      {/* Vehicle Header */}
      <div className="bg-slate-900 text-white py-3 sm:py-4">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <h1 className="font-heading font-bold text-mobile-body-lg sm:text-lg md:text-2xl" style={{ fontFamily: "'Montserrat', sans-serif" }}>
            {vehicle.year} {vehicle.make} {vehicle.model} {vehicle.trim || ''} {vehicle.title ? `• ${vehicle.title}` : ''} • {formatMileage(vehicle.mileage)} km
          </h1>
        </div>
      </div>

      {/* Lightbox Modal */}
      {lightboxOpen && (
        <ImageLightbox
          images={images}
          videoUrl={vehicle.video_url || null}
          initialIndex={vehicle.video_url ? (showVideo ? 0 : selectedImage + 1) : selectedImage}
          onClose={() => setLightboxOpen(false)}
          vehicleName={`${vehicle.year} ${vehicle.make} ${vehicle.model} ${vehicle.trim || ''}`}
        />
      )}

      {/* Main Content */}
      <div className="flex-1 py-8">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            {/* Left Column - Images & Details */}
            <div className="lg:col-span-8 space-y-6">
              {/* Main Image - Swipeable on mobile, clickable to open lightbox */}
              <div 
                className="relative rounded-lg overflow-hidden cursor-pointer group aspect-[4/3] bg-slate-100"
                style={{ touchAction: 'pan-y' }}
                onClick={() => { if (!touchRef.current.isSwiping) setLightboxOpen(true); }}
                onTouchStart={(e) => {
                  const touch = e.touches[0];
                  touchRef.current = { startX: touch.clientX, startY: touch.clientY, isSwiping: false };
                }}
                onTouchMove={(e) => {
                  const t = touchRef.current;
                  if (!t.startX) return;
                  const touch = e.touches[0];
                  const diffX = Math.abs(t.startX - touch.clientX);
                  const diffY = Math.abs(t.startY - touch.clientY);
                  if (diffX > diffY && diffX > 10) {
                    touchRef.current.isSwiping = true;
                  }
                }}
                onTouchEnd={(e) => {
                  const t = touchRef.current;
                  const touchEndX = e.changedTouches[0].clientX;
                  const diffX = t.startX - touchEndX;
                  if (Math.abs(diffX) > 50) {
                    if (vehicle.video_url && showVideo) {
                      if (diffX > 0) setShowVideo(false);
                    } else if (diffX > 0 && selectedImage < images.length - 1) {
                      setSelectedImage(selectedImage + 1);
                    } else if (diffX < 0 && selectedImage > 0) {
                      setSelectedImage(selectedImage - 1);
                    } else if (diffX < 0 && vehicle.video_url) {
                      setShowVideo(true);
                    }
                  }
                  touchRef.current = { startX: 0, startY: 0, isSwiping: false };
                }}
                data-testid="main-image-container"
              >
                    {/* Live Viewers Badge - Top Left */}
                    <LiveViewers vehicleId={vehicle.id} variant="card" />
                    
                    {/* Show video in main photo area if available, otherwise show image */}
                    {vehicle.video_url && showVideo ? (
                      <video
                        src={vehicle.video_url}
                        controls
                        autoPlay
                        muted
                        loop
                        playsInline
                        preload="metadata"
                        className="w-full h-full object-cover select-none"
                        data-testid="vehicle-main-video"
                      />
                    ) : (
                      <img
                        src={typeof images[selectedImage] === 'object' ? images[selectedImage].url : getImageUrl(images[selectedImage])}
                        alt={`${vehicle.year} ${vehicle.make} ${vehicle.model}`}
                        className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-[1.02] select-none pointer-events-none"
                        data-testid="vehicle-main-image"
                        onError={(e) => { e.target.src = defaultImages[0]; }}
                        draggable="false"
                      />
                    )}
                    
                    {/* Image counter badge */}
                    {images.length > 1 && !(vehicle.video_url && showVideo) && (
                      <div className="absolute bottom-3 right-3 bg-black/60 text-white text-xs font-medium px-2 py-1 rounded-full">
                        {selectedImage + 1} / {images.length}
                      </div>
                    )}
                    
                    {/* Swipe hint on mobile */}
                    <div className="absolute bottom-3 left-3 bg-black/60 text-white text-xs px-2 py-1 rounded-full sm:hidden">
                      ← Glisser →
                    </div>
                    
                {/* Zoom indicator overlay */}
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center pointer-events-none">
                  <div className="opacity-0 group-hover:opacity-100 transition-opacity bg-white/90 px-4 py-2 rounded-full flex items-center gap-2 shadow-lg">
                    <ZoomIn className="h-5 w-5 text-slate-700" />
                    <span className="text-sm font-medium text-slate-700">Cliquer pour agrandir</span>
                  </div>
                </div>
                {/* Overlay/Watermark - only on vehicle photos */}
                {overlaySettings.enabled && overlaySettings.image_url && typeof images[selectedImage] === 'object' && images[selectedImage].isVehicle && (
                  <img 
                    src={overlaySettings.image_url} 
                    alt="Overlay" 
                    className="absolute inset-0 w-full h-full object-cover pointer-events-none"
                  />
                )}
                {vehicle.status === 'sold' && (
                  <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                    <span className="font-heading font-bold text-4xl text-white">VENDU</span>
                  </div>
                )}
              </div>

              {/* Thumbnail Gallery */}
              {(images.length > 1 || vehicle.video_url) && (
                <div className="flex gap-2 overflow-x-auto pb-2">
                  {vehicle.video_url && (
                    <button
                      onClick={() => setShowVideo(true)}
                      className={`relative flex-shrink-0 w-24 h-16 rounded-md overflow-hidden border-2 transition-colors bg-black ${
                        showVideo ? 'border-primary' : 'border-transparent hover:border-border'
                      }`}
                      data-testid="thumbnail-video"
                    >
                      <video src={vehicle.video_url} muted playsInline preload="metadata" className="w-full h-full object-cover pointer-events-none" />
                      <span className="absolute inset-0 flex items-center justify-center bg-black/30">
                        <Play className="h-5 w-5 text-white fill-white" />
                      </span>
                    </button>
                  )}
                  {images.map((img, index) => {
                    const imgUrl = typeof img === 'object' ? img.url : img;
                    const isVehicle = typeof img === 'object' && img.isVehicle;
                    return (
                      <button
                        key={index}
                        onClick={() => { setSelectedImage(index); setShowVideo(false); }}
                        className={`relative flex-shrink-0 w-24 h-16 rounded-md overflow-hidden border-2 transition-colors ${
                          selectedImage === index && !(vehicle.video_url && showVideo) ? 'border-primary' : 'border-transparent hover:border-border'
                        }`}
                        data-testid={`thumbnail-${index}`}
                      >
                        <img 
                          src={imgUrl} 
                          alt="" 
                          className={`w-full h-full ${
                            imgUrl.includes('placeholder') || imgUrl.includes('customer-assets')
                              ? 'object-contain bg-slate-100'
                              : 'object-cover'
                          }`}
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                        {/* Overlay for vehicle images */}
                        {isVehicle && overlaySettings.enabled && overlaySettings.image_url && (
                          <img 
                            src={overlaySettings.image_url} 
                            alt="" 
                            className="absolute inset-0 w-full h-full object-contain pointer-events-none"
                          />
                        )}
                      </button>
                    );
                  })}
                </div>
              )}

              {/* Vehicle Info */}
              <div className="bg-white border border-gray-100 rounded-2xl p-4 md:p-8 shadow-sm">
                {/* Mobile: Title and Price on same row */}
                <div className="md:hidden mb-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1">
                      <span className="inline-block bg-[#3b82f6]/10 text-[#3b82f6] text-mobile-xs font-semibold px-2 py-0.5 rounded-full mb-2">{vehicle.year}</span>
                      <h1 className="font-black text-mobile-h3 text-gray-900" style={{ fontFamily: "'Montserrat', sans-serif" }} data-testid="vehicle-title-mobile">
                        {vehicle.make} {vehicle.model} {vehicle.trim && <span className="font-bold">{vehicle.trim}</span>}
                      </h1>
                      {vehicle.title && (
                        <p className="text-mobile-xs text-gray-500 font-medium mt-1">{vehicle.title}</p>
                      )}
                    </div>
                    <div className="text-right flex-shrink-0">
                      {vehicle.regular_price && vehicle.regular_price > vehicle.price ? (
                        <div className="bg-gradient-to-br from-[#0c2340]/5 to-[#007ACC]/10 border border-[#0c2340]/20 rounded-xl p-3 min-w-[140px]">
                          <span className="inline-flex items-center gap-1 bg-[#0c2340] text-white text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider mb-1.5">
                            Promo
                          </span>
                          <p className="text-xs text-gray-400 line-through font-medium" style={{ fontFamily: "'Montserrat', sans-serif" }} data-testid="vehicle-regular-price">
                            {formatPrice(vehicle.regular_price)}
                          </p>
                          <p className="font-black text-xl text-[#0c2340] tracking-tight leading-tight" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                            {formatPrice(vehicle.price)}
                          </p>
                          <p className="text-[10px] font-bold text-green-700 bg-green-100 rounded-full px-2 py-0.5 mt-1 inline-block">
                            Économisez {new Intl.NumberFormat('fr-CA', { style: 'currency', currency: 'CAD', minimumFractionDigits: 0 }).format(vehicle.regular_price - vehicle.price)}
                          </p>
                        </div>
                      ) : (
                        <p className="font-black text-mobile-h3 text-[#3b82f6] tracking-tight" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                          {formatPrice(vehicle.price)}
                        </p>
                      )}
                      <Button asChild size="sm" className="mt-2 bg-[#3b82f6] hover:bg-[#2563eb] rounded-full text-mobile-xs h-8 px-3 w-full">
                        <Link to="/credit">
                          {t('vehicle.financingOptions')}
                        </Link>
                      </Button>
                    </div>
                  </div>
                </div>
                
                {/* Mobile: Stock/VIN */}
                <div className="md:hidden mb-6">
                  <div className="flex flex-wrap gap-2 items-center">
                    {vehicle.stock_number && (
                      <span className="inline-flex items-center gap-1 bg-gray-100 text-gray-700 text-mobile-xs px-2 py-1 rounded-lg">
                        {t('vehicle.stock')} #{vehicle.stock_number}
                      </span>
                    )}
                    {vehicle.vin && (
                      <span className="inline-flex items-center gap-1 bg-gray-100 text-gray-700 text-mobile-xs px-2 py-1 rounded-lg font-mono">
                        {t('vehicle.vin')}: {vehicle.vin}
                      </span>
                    )}
                    <button
                      onClick={handleShareVehicle}
                      className="inline-flex items-center gap-1 bg-[#3b82f6]/10 text-[#3b82f6] text-mobile-xs px-2 py-1 rounded-lg hover:bg-[#3b82f6]/20 transition-colors ml-auto"
                      data-testid="share-vehicle-mobile"
                    >
                      {shareCopied ? <Check className="h-3 w-3" /> : <Share2 className="h-3 w-3" />}
                      {shareCopied ? t('vehicle.copied', 'Copié') : t('vehicle.share', 'Partager')}
                    </button>
                  </div>
                </div>

                {/* Desktop layout */}
                <div className="hidden md:block mb-8">
                  <div className="flex items-start justify-between gap-6 mb-4">
                    <div className="flex-1 min-w-0">
                      <span className="inline-block bg-[#3b82f6]/10 text-[#3b82f6] text-sm font-semibold px-3 py-1 rounded-full mb-2">{vehicle.year}</span>
                      <h1 className="font-black text-2xl lg:text-3xl text-gray-900 whitespace-nowrap" style={{ fontFamily: "'Montserrat', sans-serif" }} data-testid="vehicle-title">
                        {vehicle.make} {vehicle.model} {vehicle.trim && <span className="font-bold">{vehicle.trim}</span>}
                      </h1>
                      {vehicle.title && (
                        <p className="text-sm text-gray-500 font-medium mt-1">{vehicle.title}</p>
                      )}
                      <div className="flex flex-wrap items-center gap-2 mt-2">
                        {vehicle.stock_number && (
                          <span className="inline-flex items-center gap-1.5 bg-gray-100 text-gray-600 text-xs px-2.5 py-1 rounded-lg">
                            <svg className="w-3.5 h-3.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                            </svg>
                            {t('vehicle.stock')} #{vehicle.stock_number}
                          </span>
                        )}
                        {vehicle.vin && (
                          <span className="inline-flex items-center gap-1.5 bg-gray-100 text-gray-600 text-xs px-2.5 py-1 rounded-lg font-mono">
                            VIN: {vehicle.vin}
                          </span>
                        )}
                        <button
                          onClick={handleShareVehicle}
                          className="inline-flex items-center gap-1.5 bg-[#3b82f6]/10 text-[#3b82f6] text-xs px-2.5 py-1 rounded-lg hover:bg-[#3b82f6]/20 transition-colors"
                          data-testid="share-vehicle-desktop"
                        >
                          {shareCopied ? <Check className="h-3.5 w-3.5" /> : <Share2 className="h-3.5 w-3.5" />}
                          {shareCopied ? t('vehicle.copied', 'Copié') : t('vehicle.share', 'Partager')}
                        </button>
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0">
                      {vehicle.regular_price && vehicle.regular_price > vehicle.price ? (
                        <div className="bg-gradient-to-br from-[#0c2340]/5 to-[#007ACC]/10 border border-[#0c2340]/20 rounded-2xl p-4 min-w-[200px] shadow-sm">
                          <span className="inline-flex items-center gap-1.5 bg-[#0c2340] text-white text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider mb-2">
                            Promo
                          </span>
                          <p className="text-sm text-gray-400 line-through font-medium" style={{ fontFamily: "'Montserrat', sans-serif" }} data-testid="vehicle-regular-price-desktop">
                            {formatPrice(vehicle.regular_price)}
                          </p>
                          <p className="font-black text-3xl lg:text-4xl text-[#0c2340] tracking-tight leading-tight" style={{ fontFamily: "'Montserrat', sans-serif" }} data-testid="vehicle-price">
                            {formatPrice(vehicle.price)}
                          </p>
                          <p className="text-xs font-bold text-green-700 bg-green-100 rounded-full px-3 py-1 mt-2 inline-block">
                            Économisez {new Intl.NumberFormat('fr-CA', { style: 'currency', currency: 'CAD', minimumFractionDigits: 0 }).format(vehicle.regular_price - vehicle.price)}
                          </p>
                        </div>
                      ) : (
                        <p className="font-black text-2xl lg:text-3xl text-[#3b82f6] tracking-tight whitespace-nowrap" style={{ fontFamily: "'Montserrat', sans-serif" }} data-testid="vehicle-price">
                          {formatPrice(vehicle.price)}
                        </p>
                      )}
                      <Button asChild className="mt-3 bg-[#3b82f6] hover:bg-[#2563eb] rounded-full text-sm h-9 px-5 w-full">
                        <Link to="/credit">
                          {t('vehicle.financingOptions')}
                        </Link>
                      </Button>
                    </div>
                  </div>
                  {vehicle.status === 'pending' && (
                    <Badge className="mb-2 bg-yellow-500 text-black">Vente en Cours</Badge>
                  )}
                </div>

                {/* Quick Specs - Modern Cards */}
                <div className="grid grid-cols-3 md:grid-cols-7 gap-2">
                  <div className="bg-gradient-to-br from-gray-50 to-gray-100 p-2.5 sm:p-3 rounded-xl border border-gray-100 hover:shadow-md transition-shadow">
                    <div className="w-7 h-7 sm:w-8 sm:h-8 bg-[#3b82f6]/10 rounded-lg flex items-center justify-center mb-1.5 sm:mb-2">
                      <Gauge className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-[#3b82f6]" />
                    </div>
                    <p className="text-mobile-xs text-gray-500 uppercase tracking-wide">{t('vehicle.mileage')}</p>
                    <p className="font-bold text-gray-900 text-mobile-sm sm:text-sm">{formatMileage(vehicle.mileage)} km</p>
                  </div>
                  <div className="bg-gradient-to-br from-gray-50 to-gray-100 p-2.5 sm:p-3 rounded-xl border border-gray-100 hover:shadow-md transition-shadow">
                    <div className="w-7 h-7 sm:w-8 sm:h-8 bg-[#3b82f6]/10 rounded-lg flex items-center justify-center mb-1.5 sm:mb-2">
                      <Settings2 className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-[#3b82f6]" />
                    </div>
                    <p className="text-mobile-xs text-gray-500 uppercase tracking-wide">{t('vehicle.transmission')}</p>
                    <p className="font-bold text-gray-900 text-mobile-sm sm:text-sm">{translateTransmission(vehicle.transmission)}</p>
                  </div>
                  <div className="bg-gradient-to-br from-gray-50 to-gray-100 p-2.5 sm:p-3 rounded-xl border border-gray-100 hover:shadow-md transition-shadow">
                    <div className="w-7 h-7 sm:w-8 sm:h-8 bg-[#3b82f6]/10 rounded-lg flex items-center justify-center mb-1.5 sm:mb-2">
                      <CircleDot className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-[#3b82f6]" />
                    </div>
                    <p className="text-mobile-xs text-gray-500 uppercase tracking-wide">{t('vehicle.drivetrain')}</p>
                    <p className="font-bold text-gray-900 text-mobile-sm sm:text-sm">{translateDrivetrain(vehicle.drivetrain)}</p>
                  </div>
                  <div className="bg-gradient-to-br from-gray-50 to-gray-100 p-2.5 sm:p-3 rounded-xl border border-gray-100 hover:shadow-md transition-shadow">
                    <div className="w-7 h-7 sm:w-8 sm:h-8 bg-[#3b82f6]/10 rounded-lg flex items-center justify-center mb-1.5 sm:mb-2">
                      <Fuel className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-[#3b82f6]" />
                    </div>
                    <p className="text-mobile-xs text-gray-500 uppercase tracking-wide">{t('vehicle.fuel')}</p>
                    <p className="font-bold text-gray-900 text-mobile-sm sm:text-sm">{translateFuel(vehicle.fuel_type)}</p>
                  </div>
                  {vehicle.engine && (
                    <div className="bg-gradient-to-br from-gray-50 to-gray-100 p-2.5 sm:p-3 rounded-xl border border-gray-100 hover:shadow-md transition-shadow" data-testid="vehicle-engine">
                      <div className="w-7 h-7 sm:w-8 sm:h-8 bg-[#3b82f6]/10 rounded-lg flex items-center justify-center mb-1.5 sm:mb-2">
                        <Fuel className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-[#3b82f6]" />
                      </div>
                      <p className="text-mobile-xs text-gray-500 uppercase tracking-wide">{t('vehicle.engine')}</p>
                      <p className="font-bold text-gray-900 text-mobile-sm sm:text-sm">{vehicle.engine}</p>
                    </div>
                  )}
                  <div className="bg-gradient-to-br from-gray-50 to-gray-100 p-2.5 sm:p-3 rounded-xl border border-gray-100 hover:shadow-md transition-shadow">
                    <div className="w-7 h-7 sm:w-8 sm:h-8 bg-[#3b82f6]/10 rounded-lg flex items-center justify-center mb-1.5 sm:mb-2">
                      <Car className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-[#3b82f6]" />
                    </div>
                    <p className="text-mobile-xs text-gray-500 uppercase tracking-wide">{t('vehicle.bodyType')}</p>
                    <p className="font-bold text-gray-900 text-mobile-sm sm:text-sm">{translateBodyType(vehicle.body_type)}</p>
                  </div>
                  <div className="bg-gradient-to-br from-gray-50 to-gray-100 p-2.5 sm:p-3 rounded-xl border border-gray-100 hover:shadow-md transition-shadow">
                    <div className="w-7 h-7 sm:w-8 sm:h-8 bg-[#3b82f6]/10 rounded-lg flex items-center justify-center mb-1.5 sm:mb-2">
                      <Palette className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-[#3b82f6]" />
                    </div>
                    <p className="text-mobile-xs text-gray-500 uppercase tracking-wide">{t('vehicle.exterior')}</p>
                    <p className="font-bold text-gray-900 text-mobile-sm sm:text-sm capitalize">{vehicle.exterior_color || '-'}</p>
                  </div>
                  <div className="bg-gradient-to-br from-gray-50 to-gray-100 p-2.5 sm:p-3 rounded-xl border border-gray-100 hover:shadow-md transition-shadow">
                    <div className="w-7 h-7 sm:w-8 sm:h-8 bg-[#3b82f6]/10 rounded-lg flex items-center justify-center mb-1.5 sm:mb-2">
                      <Palette className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-[#3b82f6]" />
                    </div>
                    <p className="text-mobile-xs text-gray-500 uppercase tracking-wide">{t('vehicle.interior')}</p>
                    <p className="font-bold text-gray-900 text-mobile-sm sm:text-sm capitalize">{vehicle.interior_color || '-'}</p>
                  </div>
                </div>
              </div>

                {/* Description */}
                {vehicle.description && (
                  <div className="bg-white border border-gray-100 rounded-2xl p-6 mt-6 shadow-sm">
                    <h2 className="font-bold text-xl mb-3 text-gray-900">{t('vehicle.description')}</h2>
                    {translatingDesc ? (
                      <p className="text-gray-400 leading-relaxed italic animate-pulse">{vehicle.description}</p>
                    ) : (
                      <p className="text-gray-600 leading-relaxed">{translatedDesc || vehicle.description}</p>
                    )}
                  </div>
                )}

                {/* 100-Point Inspection Report + Ready-to-Drive Score */}
                <div className="mt-6">
                  <InspectionReport vehicleId={vehicle.id} />
                </div>

                {/* CARFAX Section */}
                {(hasCarfaxReport(vehicle) || isCarfaxOneOwner(vehicle) || isCarfaxAccidentFree(vehicle)) && (
                  <div className="bg-white border border-gray-100 rounded-2xl p-6 mt-6 shadow-sm" data-testid="carfax-section">
                    <div className="flex items-center gap-3 mb-4">
                      <h2 className="font-bold text-xl text-gray-900">{t('vehicle.vehicleHistory')}</h2>
                    </div>
                    <CarfaxReport vehicle={vehicle} height={42} />
                  </div>
                )}

                {/* Features & Inspection Tabs */}
                <div className="mt-6">
                  <div className="bg-white border border-gray-100 rounded-2xl shadow-sm">
                    {/* Tab Headers - Modern Style */}
                    <div className="bg-gray-50 p-1.5 flex gap-1 rounded-t-2xl">
                    <button
                      onClick={() => setActiveInfoTab('equipements')}
                      className={`flex-1 flex items-center justify-center gap-2 py-4 px-6 rounded-xl font-semibold text-sm transition-all duration-300 ${
                        activeInfoTab === 'equipements' 
                          ? 'bg-white text-gray-900 shadow-md' 
                          : 'text-gray-500 hover:text-gray-700 hover:bg-white/50'
                      }`}
                    >
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors ${
                        activeInfoTab === 'equipements' ? 'bg-[#3b82f6]' : 'bg-gray-200'
                      }`}>
                        <Check className={`h-4 w-4 ${activeInfoTab === 'equipements' ? 'text-white' : 'text-gray-500'}`} />
                      </div>
                      <span>{t('vehicle.equipment')}</span>
                      {vehicle.features?.length > 0 && (
                        <span className={`text-xs px-2.5 py-1 rounded-full font-bold ${
                          activeInfoTab === 'equipements' ? 'bg-[#3b82f6] text-white' : 'bg-gray-200 text-gray-600'
                        }`}>
                          {vehicle.features.length}
                        </span>
                      )}
                    </button>
                    <button
                      onClick={() => setActiveInfoTab('inspection')}
                      className={`flex-1 flex items-center justify-center gap-2 py-4 px-3 md:px-6 rounded-xl font-semibold text-sm transition-all duration-300 ${
                        activeInfoTab === 'inspection' 
                          ? 'bg-white text-gray-900 shadow-md' 
                          : 'text-gray-500 hover:text-gray-700 hover:bg-white/50'
                      }`}
                    >
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors flex-shrink-0 ${
                        activeInfoTab === 'inspection' ? 'bg-[#3b82f6]' : 'bg-gray-200'
                      }`}>
                        <svg className={`w-4 h-4 ${activeInfoTab === 'inspection' ? 'text-white' : 'text-gray-500'}`} fill="currentColor" viewBox="0 0 24 24">
                          <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm-2 16l-4-4 1.41-1.41L10 14.17l6.59-6.59L18 9l-8 8z"/>
                        </svg>
                      </div>
                      <span className="hidden sm:inline">{t('vehicle.inspection')}</span>
                      <span className="sm:hidden">{t('vehicle.inspectionShort')}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-bold flex-shrink-0 ${
                        activeInfoTab === 'inspection' ? 'bg-[#3b82f6] text-white' : 'bg-gray-200 text-gray-600'
                      }`}>
                        150+
                      </span>
                    </button>
                  </div>

                  {/* Tab Content */}
                  <div>
                    {activeInfoTab === 'equipements' && vehicle.features?.length > 0 && (
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 p-3 md:p-6">
                        {vehicle.features.map((feature, index) => (
                          <div key={index} className="flex items-center gap-3 bg-gradient-to-r from-gray-50 to-white border border-gray-100 hover:border-[#3b82f6]/30 hover:shadow-sm px-4 py-3 rounded-xl transition-all">
                            <div className="w-6 h-6 bg-gradient-to-br from-[#3b82f6] to-[#2563eb] rounded-full flex items-center justify-center flex-shrink-0 shadow-sm">
                              <Check className="h-3.5 w-3.5 text-white" />
                            </div>
                            <span className="text-gray-700 text-sm">{feature}</span>
                          </div>
                        ))}
                      </div>
                    )}

                    {activeInfoTab === 'inspection' && (
                      <div className="p-4 md:p-6">
                        {/* Mobile: Simple clean layout without box */}
                        <div className="md:hidden text-center pl-8 pr-4">
                          <div className="w-16 h-16 bg-[#3b82f6] rounded-2xl flex items-center justify-center mx-auto mb-4">
                            <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm-2 16l-4-4 1.41-1.41L10 14.17l6.59-6.59L18 9l-8 8z"/>
                            </svg>
                          </div>
                          <span className="inline-block bg-[#3b82f6]/10 text-[#3b82f6] text-xs font-bold px-3 py-1 rounded-full mb-3">VÉRIFIÉ</span>
                          <h3 className="text-2xl font-black text-slate-900 mb-2">Véhicule Inspecté</h3>
                          <p className="text-slate-600 text-sm mb-6">{t('vehicle.inspectionDesc')}</p>
                          
                          {/* Stats Grid */}
                          <div className="grid grid-cols-2 gap-3 mb-6">
                            <div className="bg-slate-50 rounded-xl p-4 text-center">
                              <p className="text-2xl font-black text-[#3b82f6]">150+</p>
                              <p className="text-[10px] text-slate-500 uppercase tracking-wide">Points</p>
                            </div>
                            <div className="bg-slate-50 rounded-xl p-4 text-center">
                              <p className="text-2xl font-black text-[#3b82f6]">100%</p>
                              <p className="text-[10px] text-slate-500 uppercase tracking-wide">Conforme</p>
                            </div>
                            <div className="bg-slate-50 rounded-xl p-4 text-center">
                              <div className="text-2xl text-green-500 mb-0.5">✓</div>
                              <p className="text-[10px] text-slate-500 uppercase tracking-wide">{t('vehicle.mechanical')}</p>
                            </div>
                            <div className="bg-slate-50 rounded-xl p-4 text-center">
                              <div className="text-2xl text-green-500 mb-0.5">✓</div>
                              <p className="text-[10px] text-slate-500 uppercase tracking-wide">{t('vehicle.safety')}</p>
                            </div>
                          </div>
                          
                          <a
                            href={`${API}/vehicles/${vehicle.id}/inspection-report`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center justify-center gap-2 bg-[#3b82f6] text-white font-bold px-6 py-3 rounded-xl w-full"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                            Voir le rapport
                          </a>
                        </div>
                        
                        {/* Desktop: Blue box card */}
                        <div className="hidden md:block bg-gradient-to-br from-[#3b82f6] via-[#2563eb] to-[#1d4ed8] rounded-2xl p-8 text-white relative overflow-hidden">
                          {/* Background decorations */}
                          <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2"></div>
                          <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2"></div>
                          
                          <div className="relative text-center md:text-left">
                            {/* Header - Centered on mobile */}
                            <div className="flex flex-col md:flex-row items-center md:items-start gap-3 md:gap-5 mb-6">
                              <div className="w-16 h-16 md:w-20 md:h-20 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center shadow-lg">
                                <svg className="w-8 h-8 md:w-10 md:h-10" fill="currentColor" viewBox="0 0 24 24">
                                  <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm-2 16l-4-4 1.41-1.41L10 14.17l6.59-6.59L18 9l-8 8z"/>
                                </svg>
                              </div>
                              <div className="flex-1">
                                <span className="inline-block bg-white/20 backdrop-blur-sm text-xs font-bold px-3 py-1 rounded-full mb-2">VÉRIFIÉ</span>
                                <h3 className="text-2xl md:text-3xl font-black mb-2">Véhicule Inspecté</h3>
                                <p className="text-blue-100 text-sm md:text-lg leading-snug max-w-md mx-auto md:mx-0">{t('vehicle.inspectionDesc')}</p>
                              </div>
                            </div>
                            
                            {/* Stats Grid - 2x2 centered on mobile */}
                            <div className="grid grid-cols-2 gap-3 mb-6 max-w-xs mx-auto md:max-w-none md:grid-cols-4 md:gap-4">
                              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 text-center border border-white/10">
                                <p className="text-3xl md:text-4xl font-black mb-1">150<span className="text-lg md:text-xl">+</span></p>
                                <p className="text-[11px] md:text-xs text-blue-200 uppercase tracking-wide">Points</p>
                              </div>
                              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 text-center border border-white/10">
                                <p className="text-3xl md:text-4xl font-black mb-1">100<span className="text-lg md:text-xl">%</span></p>
                                <p className="text-[11px] md:text-xs text-blue-200 uppercase tracking-wide">Conforme</p>
                              </div>
                              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 text-center border border-white/10">
                                <div className="text-3xl md:text-4xl mb-1">✓</div>
                                <p className="text-[11px] md:text-xs text-blue-200 uppercase tracking-wide">{t('vehicle.mechanical')}</p>
                              </div>
                              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 text-center border border-white/10">
                                <div className="text-3xl md:text-4xl mb-1">✓</div>
                                <p className="text-[11px] md:text-xs text-blue-200 uppercase tracking-wide">{t('vehicle.safety')}</p>
                              </div>
                            </div>
                            
                            {/* CTA Button */}
                            <a
                              href={`${API}/vehicles/${vehicle.id}/inspection-report`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center justify-center gap-2 bg-white text-[#3b82f6] font-bold px-6 py-3.5 rounded-xl hover:bg-blue-50 transition-all shadow-xl text-base w-auto"
                              data-testid="inspection-report-link"
                            >
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                              </svg>
                              Voir le rapport d'inspection
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                              </svg>
                            </a>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column - Contact Forms */}
            <div className="lg:col-span-4 space-y-6">
              {/* Reserve CTA — Primary action */}
              {vehicle.status !== 'sold' && (
                <div className="bg-gradient-to-br from-emerald-500 via-emerald-600 to-teal-600 rounded-2xl shadow-xl p-5 text-white sticky top-24 z-10">
                  <p className="text-xs uppercase tracking-widest font-bold opacity-90 mb-1">{t('carvana.reserve.badge', '100% en ligne')}</p>
                  <h3 className="text-2xl font-black mb-2">{t('carvana.reserve.sidebarTitle', 'Réservez ce véhicule')}</h3>
                  <p className="text-sm opacity-90 mb-4">{t('carvana.reserve.sidebarDesc', 'Dépôt remboursable de 499$ • Bloqué 30 min')}</p>
                  <button
                    onClick={() => setReserveOpen(true)}
                    className="w-full bg-white text-emerald-700 font-black py-3.5 rounded-xl hover:bg-emerald-50 transition-colors flex items-center justify-center gap-2"
                    data-testid="reserve-now-btn"
                  >
                    {t('carvana.reserve.cta', 'Réserver maintenant — 499$')}
                  </button>
                </div>
              )}

              <div className="bg-white border border-gray-100 rounded-2xl shadow-xl overflow-hidden">
                {/* Header with gradient */}
                <div className="bg-gradient-to-r from-[#3b82f6] to-[#1d4ed8] p-5 text-white">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
                      <Phone className="h-5 w-5" />
                    </div>
                    <div>
                      <p className="text-blue-100 text-xs uppercase tracking-wider font-medium">{t('vehicle.contactUs')}</p>
                      <p className="font-bold text-lg">(833) 995-6477</p>
                    </div>
                  </div>
                </div>

                {/* Tabs */}
                <div className="flex bg-gray-50 p-1 mx-4 mt-4 rounded-xl">
                  <button
                    onClick={() => setActiveTab('inquiry')}
                    className={`flex-1 py-3 px-4 text-sm font-semibold rounded-lg transition-all duration-200 flex items-center justify-center gap-2 ${
                      activeTab === 'inquiry' 
                        ? 'bg-white text-[#3b82f6] shadow-md' 
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    <Mail className="h-4 w-4" />
                    {t('vehicle.inquiryTab')}
                  </button>
                  <button
                    onClick={() => setActiveTab('testdrive')}
                    className={`flex-1 py-3 px-4 text-sm font-semibold rounded-lg transition-all duration-200 flex items-center justify-center gap-2 ${
                      activeTab === 'testdrive' 
                        ? 'bg-white text-[#3b82f6] shadow-md' 
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    <Car className="h-4 w-4" />
                    {t('vehicle.testDriveTab')}
                  </button>
                </div>

                <div className="p-5">
                  {/* Inquiry Form */}
                  {activeTab === 'inquiry' && (
                    <>
                      <div className="mb-5">
                        <h2 className="font-heading font-bold text-xl text-gray-900">{t('vehicle.interestedInVehicle')}</h2>
                        <p className="text-sm text-gray-500 mt-1">
                          {t('vehicle.fillFormContact')}
                        </p>
                        <div className="mt-3">
                          <GoogleRating variant="compact" />
                        </div>
                      </div>

                      <form onSubmit={handleInquirySubmit} className="space-y-4" data-testid="inquiry-form">
                        <div>
                          <Label htmlFor="name" className="text-gray-700 font-medium">{t('vehicle.fullName')}</Label>
                          <Input
                            id="name"
                            value={inquiryForm.name}
                            onChange={(e) => setInquiryForm({ ...inquiryForm, name: e.target.value })}
                            placeholder={t('form.namePlaceholder')}
                            required
                            className="mt-1.5 bg-gray-50 border-gray-200 focus:border-[#3b82f6] focus:ring-[#3b82f6]/20 rounded-xl h-11"
                            data-testid="inquiry-name"
                          />
                        </div>
                        <div>
                          <Label htmlFor="email" className="text-gray-700 font-medium">{t('vehicle.email')}</Label>
                          <Input
                            id="email"
                            type="email"
                            value={inquiryForm.email}
                            onChange={(e) => setInquiryForm({ ...inquiryForm, email: e.target.value })}
                            placeholder="jean@exemple.com"
                            required
                            className="mt-1.5 bg-gray-50 border-gray-200 focus:border-[#3b82f6] focus:ring-[#3b82f6]/20 rounded-xl h-11"
                            data-testid="inquiry-email"
                          />
                        </div>
                        <div>
                          <Label htmlFor="phone" className="text-gray-700 font-medium">{t('vehicle.phone')}</Label>
                          <Input
                            id="phone"
                            type="tel"
                            value={inquiryForm.phone}
                            onChange={(e) => setInquiryForm({ ...inquiryForm, phone: e.target.value })}
                            placeholder="(514) 555-1234"
                            required
                            className="mt-1.5 bg-gray-50 border-gray-200 focus:border-[#3b82f6] focus:ring-[#3b82f6]/20 rounded-xl h-11"
                            data-testid="inquiry-phone"
                          />
                        </div>
                        <div>
                          <Label htmlFor="message" className="text-gray-700 font-medium">{t('vehicle.messageLabel')}</Label>
                          <Textarea
                            id="message"
                            value={inquiryForm.message}
                            onChange={(e) => setInquiryForm({ ...inquiryForm, message: e.target.value })}
                            placeholder="Je suis intéressé par ce véhicule..."
                            rows={3}
                            required
                            className="mt-1.5 bg-gray-50 border-gray-200 focus:border-[#3b82f6] focus:ring-[#3b82f6]/20 rounded-xl resize-none"
                            data-testid="inquiry-message"
                          />
                        </div>
                        <Button 
                          type="submit" 
                          className="w-full bg-gradient-to-r from-[#3b82f6] to-[#2563eb] hover:from-[#2563eb] hover:to-[#1d4ed8] text-white font-semibold h-12 rounded-xl shadow-lg shadow-blue-500/25 transition-all duration-200 hover:shadow-xl hover:shadow-blue-500/30 hover:-translate-y-0.5"
                          disabled={submitting}
                          data-testid="inquiry-submit"
                        >
                          {submitting ? t('vehicle.sending') : t('vehicle.sendRequest')}
                        </Button>
                      </form>
                    </>
                  )}

                  {/* Test Drive Form */}
                  {activeTab === 'testdrive' && (
                    <>
                      <div className="mb-5">
                        <h2 className="font-heading font-bold text-xl text-gray-900">{t('vehicle.bookTestDrive')}</h2>
                        <p className="text-sm text-gray-500 mt-1">
                          Essayez ce véhicule! Choisissez une date et heure qui vous convient.
                        </p>
                      </div>

                      <form onSubmit={handleTestDriveSubmit} className="space-y-4" data-testid="testdrive-form">
                        <div>
                          <Label htmlFor="td-name" className="text-gray-700 font-medium">{t('vehicle.fullName')}</Label>
                          <Input
                            id="td-name"
                            value={testDriveForm.name}
                            onChange={(e) => setTestDriveForm({ ...testDriveForm, name: e.target.value })}
                            placeholder={t('form.namePlaceholder')}
                            required
                            className="mt-1.5 bg-gray-50 border-gray-200 focus:border-[#3b82f6] focus:ring-[#3b82f6]/20 rounded-xl h-11"
                            data-testid="testdrive-name"
                          />
                        </div>
                        <div>
                          <Label htmlFor="td-email" className="text-gray-700 font-medium">{t('vehicle.email')}</Label>
                          <Input
                            id="td-email"
                            type="email"
                            value={testDriveForm.email}
                            onChange={(e) => setTestDriveForm({ ...testDriveForm, email: e.target.value })}
                            placeholder="jean@exemple.com"
                            required
                            className="mt-1.5 bg-gray-50 border-gray-200 focus:border-[#3b82f6] focus:ring-[#3b82f6]/20 rounded-xl h-11"
                            data-testid="testdrive-email"
                          />
                        </div>
                        <div>
                          <Label htmlFor="td-phone" className="text-gray-700 font-medium">{t('vehicle.phone')}</Label>
                          <Input
                            id="td-phone"
                            type="tel"
                            value={testDriveForm.phone}
                            onChange={(e) => setTestDriveForm({ ...testDriveForm, phone: e.target.value })}
                            placeholder="(514) 555-1234"
                            required
                            className="mt-1.5 bg-gray-50 border-gray-200 focus:border-[#3b82f6] focus:ring-[#3b82f6]/20 rounded-xl h-11"
                            data-testid="testdrive-phone"
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <Label htmlFor="td-date" className="text-gray-700 font-medium">{t('vehicle.preferredDate')}</Label>
                            <Input
                              id="td-date"
                              type="date"
                              value={testDriveForm.preferred_date}
                              onChange={(e) => setTestDriveForm({ ...testDriveForm, preferred_date: e.target.value })}
                              required
                              min={new Date().toISOString().split('T')[0]}
                              className="mt-1.5 bg-gray-50 border-gray-200 focus:border-[#3b82f6] focus:ring-[#3b82f6]/20 rounded-xl h-11"
                              data-testid="testdrive-date"
                            />
                          </div>
                          <div>
                            <Label htmlFor="td-time" className="text-gray-700 font-medium">{t('vehicle.preferredTime')}</Label>
                            <select
                              id="td-time"
                              value={testDriveForm.preferred_time}
                              onChange={(e) => setTestDriveForm({ ...testDriveForm, preferred_time: e.target.value })}
                              required
                              className="w-full h-11 px-3 mt-1.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:border-[#3b82f6] focus:ring-2 focus:ring-[#3b82f6]/20 outline-none transition-all"
                              data-testid="testdrive-time"
                            >
                              <option value="">{t('form.selectTime')}</option>
                              <option value="09:00">09:00</option>
                              <option value="10:00">10:00</option>
                              <option value="11:00">11:00</option>
                              <option value="12:00">12:00</option>
                              <option value="13:00">13:00</option>
                              <option value="14:00">14:00</option>
                              <option value="15:00">15:00</option>
                              <option value="16:00">16:00</option>
                              <option value="17:00">17:00</option>
                              <option value="18:00">18:00</option>
                              <option value="19:00">19:00</option>
                            </select>
                          </div>
                        </div>
                        <div>
                          <Label htmlFor="td-message" className="text-gray-700 font-medium">{t('vehicle.messageOptional')}</Label>
                          <Textarea
                            id="td-message"
                            value={testDriveForm.message}
                            onChange={(e) => setTestDriveForm({ ...testDriveForm, message: e.target.value })}
                            placeholder="Questions ou demandes spéciales..."
                            rows={2}
                            className="mt-1.5 bg-gray-50 border-gray-200 focus:border-[#3b82f6] focus:ring-[#3b82f6]/20 rounded-xl resize-none"
                            data-testid="testdrive-message"
                          />
                        </div>
                        <Button 
                          type="submit" 
                          className="w-full bg-gradient-to-r from-[#3b82f6] to-[#2563eb] hover:from-[#2563eb] hover:to-[#1d4ed8] text-white font-semibold h-12 rounded-xl shadow-lg shadow-blue-500/25 transition-all duration-200 hover:shadow-xl hover:shadow-blue-500/30 hover:-translate-y-0.5"
                          disabled={submitting}
                          data-testid="testdrive-submit"
                        >
                        <Car className="h-4 w-4 mr-2" />
                        {submitting ? t('vehicle.booking') : t('vehicle.bookTestDrive')}
                      </Button>
                    </form>
                  </>
                )}
                </div>

                {/* Contact Info Footer */}
                <div className="bg-gray-50 px-5 py-4 border-t border-gray-100">
                  <div className="flex flex-wrap items-center justify-center gap-4 text-sm">
                    <a href="mailto:info@entrepotautomontreal.ca" className="flex items-center gap-2 text-gray-600 hover:text-[#3b82f6] transition-colors">
                      <Mail className="h-4 w-4" />
                      info@entrepotautomontreal.ca
                    </a>
                    <div className="flex items-center gap-2 text-gray-500">
                      <MapPin className="h-4 w-4" />
                      1007 boul. René-Lévesque, Verdun
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Similar Vehicles Section */}
      {similarVehicles.length > 0 && (
        <section className="relative bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 py-16 overflow-hidden">
          {/* Background Pattern */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-0 left-0 w-96 h-96 bg-[#3b82f6] rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2"></div>
            <div className="absolute bottom-0 right-0 w-96 h-96 bg-[#3b82f6] rounded-full blur-3xl translate-x-1/2 translate-y-1/2"></div>
          </div>
          
          <div className="relative max-w-7xl mx-auto px-4 md:px-8">
            <div className="flex items-end justify-between mb-10">
              <div>
                <div className="flex items-center gap-3 mb-3">
                  <div className="h-1 w-12 bg-[#3b82f6] rounded-full"></div>
                  <span className="text-[#3b82f6] font-semibold text-sm uppercase tracking-wider">{t('vehicle.comparable')}</span>
                </div>
                <h2 className="text-3xl font-bold text-white">{t('vehicle.similarVehicles')}</h2>
                <p className="text-slate-400 mt-2">{t('vehicle.similarDesc')}</p>
              </div>
              <Link 
                to="/inventaire" 
                className="bg-white/10 hover:bg-white/20 text-white font-medium px-5 py-2.5 rounded-full flex items-center gap-2 transition-colors border border-white/20"
              >
                {t('vehicle.seeInventory')}
                <ChevronRight className="h-4 w-4" />
              </Link>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {similarVehicles.map((v) => (
                <Link 
                  key={v.id}
                  to={`/vehicle/${v.id}`}
                  className="bg-white rounded-2xl overflow-hidden hover:shadow-2xl hover:shadow-blue-500/20 hover:-translate-y-2 transition-all duration-300 group"
                >
                  <div className="relative aspect-[16/11] overflow-hidden bg-gray-100">
                    <img
                      src={v.images?.[0]?.includes('image.autousagee.ca') 
                        ? `${API}/proxy/thumbnail?url=${encodeURIComponent(v.images[0])}`
                        : v.images?.[0] || 'https://images.unsplash.com/photo-1494976388531-d1058494cdd8?w=600'}
                      alt={`${v.year} ${v.make} ${v.model}`}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    {v.make === vehicle?.make && (
                      <span className="absolute top-3 left-3 bg-[#3b82f6] text-white text-xs px-3 py-1.5 rounded-full font-semibold shadow-lg">
                        {t('vehicle.sameBrand')}
                      </span>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
                  </div>
                  <div className="p-5">
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <h3 className="font-bold text-gray-900 group-hover:text-[#3b82f6] transition-colors">
                        {v.year} {v.make} {v.model} {v.trim || ''}
                      </h3>
                    </div>
                    {v.title && <p className="text-gray-500 text-sm uppercase mb-3">{v.title}</p>}
                    <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                      <span className="flex items-center gap-1.5 text-gray-500 text-sm">
                        <Gauge className="h-4 w-4" />
                        {new Intl.NumberFormat('fr-CA').format(v.mileage)} km
                      </span>
                      <span className="font-bold text-[#3b82f6] text-lg">
                        {(!v.price || v.price === 0) ? t('vehicle.callDealer') : new Intl.NumberFormat(i18n.language === 'en' ? 'en-CA' : 'fr-CA', { style: 'currency', currency: 'CAD', minimumFractionDigits: 0 }).format(v.price)}
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <Footer />

      {/* Mobile Sticky CTA Bar */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 px-4 py-3 flex gap-3 lg:hidden z-[60] shadow-[0_-4px_20px_rgba(0,0,0,0.15)]">
        <a 
          href="tel:+18339956477" 
          className="flex-1 flex items-center justify-center gap-2 bg-[#3b82f6] text-white font-bold py-4 rounded-xl text-base active:scale-95 transition-transform"
        >
          <Phone className="h-5 w-5" />
          {t('vehicle.callDealer')}
        </a>
        <button 
          onClick={() => {
            setReserveOpen(true);
          }}
          className="flex-1 flex items-center justify-center gap-2 bg-emerald-600 text-white font-bold py-4 rounded-xl text-base active:scale-95 transition-transform"
          data-testid="reserve-mobile-cta"
        >
          <Car className="h-5 w-5" />
          {t('carvana.reserve.mobileCta', 'Réserver')}
        </button>
      </div>
      
      {/* Spacer for mobile sticky bar */}
      <div className="h-24 lg:hidden" />
      
      {/* Recently Viewed Widget */}
      <RecentlyViewed currentVehicleId={id} />

      {/* Reserve Modal */}
      <ReserveModal vehicle={vehicle} open={reserveOpen} onClose={() => setReserveOpen(false)} />
    </div>
  );
};

export default function VehicleDetailPageWithBoundary() {
  return (
    <VehicleErrorBoundary>
      <VehicleDetailPage />
    </VehicleErrorBoundary>
  );
}
